import { useEffect, useRef } from "react";
import { EditOutlined, LockOutlined, CloseOutlined } from "@ant-design/icons";

export interface CustomPriceRowProps {
  name: string;
  quantity: number;
  /** Цената от менюто — базата, спрямо която BE смята процента. */
  menuPrice: number;
  /** Цената в момента (менюто след вече приложената корекция). */
  currentPrice: number;
  /** Заключена цена → редът е само за четене (катинар вместо молив). */
  locked: boolean;
  /** Добавка → отместен ред с водещо „＋". */
  isAddition?: boolean;
  /** Текстът в полето; `undefined` = редът не се редактира. */
  draft?: string;
  /** Стойността в полето не е валидна цена (0 / над двойната). */
  invalid?: boolean;
  /** Подсказка към бутона за връщане на цената от менюто. */
  restoreLabel: string;
  onStartEdit: () => void;
  onDraftChange: (next: string) => void;
  onCancelEdit: () => void;
}

const formatQuantity = (quantity: number): string =>
  quantity % 1 === 0 ? quantity.toFixed(0) : quantity.toFixed(3);

/** Ред в модала „Промяна на цена" — продукт или добавка. Моливът вдясно отваря
 *  поле за нова единична цена; при заключена цена на негово място стои катинар.
 *  Задрасканата цена от менюто е кликаема, докато полето е отворено — връща я. */
export const CustomPriceRow = ({
  name,
  quantity,
  menuPrice,
  currentPrice,
  locked,
  isAddition,
  draft,
  invalid,
  restoreLabel,
  onStartEdit,
  onDraftChange,
  onCancelEdit,
}: CustomPriceRowProps) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const isEditing = draft !== undefined;
  const isChanged = currentPrice !== menuPrice;

  // Полето се отваря фокусирано и селектирано — писането започва веднага.
  useEffect(() => {
    if (!isEditing) return;
    inputRef.current?.focus();
    inputRef.current?.select();
  }, [isEditing]);

  return (
    <div
      className={`flex items-center gap-2 border-b border-pos-rowline px-3 py-2 last:border-b-0 ${
        isAddition ? "pl-7" : ""
      }`}
    >
      <span
        title={name}
        className={`min-w-0 flex-1 truncate ${
          isAddition
            ? "text-[12.5px] text-pos-addon-ink"
            : "text-[13.5px] font-semibold text-pos-ink"
        }`}
      >
        {isAddition && <span className="mr-1 text-pos-disc-ink">＋</span>}
        {name}
      </span>

      {/* Количеството се чете заедно с цената: „5 × 8.50 €" */}
      {quantity !== 1 && (
        <span className="shrink-0 font-pos-mono text-[13px] text-pos-muted">
          {formatQuantity(quantity)} ×
        </span>
      )}

      {/* Цената от менюто остава видима при променен ред; докато полето е
          отворено е и бутон — връща я в него. */}
      {isChanged &&
        (isEditing ? (
          <button
            type="button"
            title={restoreLabel}
            onClick={() => onDraftChange(menuPrice.toFixed(2))}
            className="shrink-0 cursor-pointer border-0 bg-transparent p-0 font-pos-mono text-[11px] text-pos-muted line-through hover:text-pos-accent-ink"
          >
            {menuPrice.toFixed(2)}
          </button>
        ) : (
          <span className="shrink-0 font-pos-mono text-[11px] text-pos-muted line-through">
            {menuPrice.toFixed(2)}
          </span>
        ))}

      {isEditing ? (
        <>
          <span
            className={`inline-flex h-8 w-[104px] shrink-0 items-center gap-1 rounded-lg border bg-pos-card px-2 transition-colors focus-within:ring-[3px] focus-within:ring-pos-accent/15 ${
              invalid
                ? "border-pos-danger-brd"
                : "border-pos-ctl focus-within:border-pos-accent"
            }`}
          >
            <input
              ref={inputRef}
              value={draft}
              onChange={(e) => onDraftChange(e.target.value)}
              onBlur={() => {
                // В покой цената стои ВИНАГИ с 2 знака: „12" → „12.00".
                const parsed = Number(draft.replace(",", "."));
                if (draft.trim() !== "" && Number.isFinite(parsed))
                  onDraftChange(parsed.toFixed(2));
              }}
              inputMode="decimal"
              className="min-w-0 flex-1 border-0 bg-transparent text-right font-pos-mono text-[14px] font-bold text-pos-ink outline-none"
            />
            <span className="font-pos-mono text-[13px] text-pos-muted">€</span>
          </span>
          <button
            type="button"
            onClick={onCancelEdit}
            className="grid h-8 w-8 shrink-0 cursor-pointer place-items-center rounded-lg border border-pos-line2 bg-pos-card text-[12px] text-pos-dim transition-colors hover:text-pos-ink"
          >
            <CloseOutlined />
          </button>
        </>
      ) : (
        <>
          <span className="shrink-0 font-pos-mono text-sm font-semibold text-pos-ink2">
            {currentPrice.toFixed(2)} €
          </span>
          {locked ? (
            <span className="grid h-8 w-8 shrink-0 place-items-center text-[13px] text-pos-faint">
              <LockOutlined />
            </span>
          ) : (
            <button
              type="button"
              onClick={onStartEdit}
              className="grid h-8 w-8 shrink-0 cursor-pointer place-items-center rounded-lg border border-pos-ctl bg-pos-stepper text-[13px] text-pos-accent-ink transition-colors hover:bg-pos-marked"
            >
              <EditOutlined />
            </button>
          )}
        </>
      )}
    </div>
  );
};
